import { useState } from 'react';
import axios from 'axios';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  formateur: { label: 'Formateur', color: 'bg-green-100 text-green-700 border-green-200' },
  tuteur: { label: 'Tuteur', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  entreprise: { label: 'Entreprise', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  responsable_pedagogique: { label: 'Resp. Peda.', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  stagiaire: { label: 'Stagiaire', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  centre: { label: 'Centre', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  moniteur_vnm: { label: 'Moniteur', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

const ASSIGNABLE_ROLES = ['formateur', 'tuteur', 'entreprise', 'responsable_pedagogique'];

const RoleBadges = ({ userId, roles = [], onRolesChange }) => {
  const [showAdd, setShowAdd] = useState(false);

  const availableRoles = ASSIGNABLE_ROLES.filter(r => !roles.includes(r));

  const handleAddRole = async (e, role) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const res = await axios.post(`/users/${userId}/add-role`, { role });
      toast.success(res.data.message);
      if (onRolesChange) onRolesChange(res.data.roles);
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveRole = async (e, role) => {
    e.stopPropagation();
    e.preventDefault();
    if (roles.length <= 1) {
      toast.error('Impossible de retirer le dernier role');
      return;
    }
    try {
      const res = await axios.post(`/users/${userId}/remove-role`, { role });
      toast.success(res.data.message);
      if (onRolesChange) onRolesChange(res.data.roles);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleAdd = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowAdd(!showAdd);
  };

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap mt-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid={`role-badges-${userId}`}
    >
      {roles.map(r => {
        const cfg = ROLE_CONFIG[r] || { label: r, color: 'bg-slate-100 text-slate-600 border-slate-200' };
        return (
          <span key={r} className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color}`}>
            {cfg.label}
            {roles.length > 1 && (
              <button
                onClick={(e) => handleRemoveRole(e, r)}
                onMouseDown={(e) => e.stopPropagation()}
                className="hover:opacity-70 ml-0.5 cursor-pointer"
                data-testid={`remove-role-${r}-${userId}`}
              >
                <X className="h-3 w-3 pointer-events-none" />
              </button>
            )}
          </span>
        );
      })}
      {availableRoles.length > 0 && (
        <div className="relative">
          <button
            onClick={handleToggleAdd}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 hover:bg-[#8DC63F] hover:text-white text-slate-500 transition-colors cursor-pointer"
            data-testid={`add-role-btn-${userId}`}
          >
            <Plus className="h-3.5 w-3.5 pointer-events-none" />
          </button>
          {showAdd && (
            <div
              className="absolute top-8 left-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-1 min-w-[180px]"
              onClick={(e) => e.stopPropagation()}
              data-testid={`role-dropdown-${userId}`}
            >
              {availableRoles.map(r => (
                <button
                  key={r}
                  onClick={(e) => handleAddRole(e, r)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-slate-100 transition-colors cursor-pointer"
                  data-testid={`add-role-option-${r}-${userId}`}
                >
                  {ROLE_CONFIG[r]?.label || r}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoleBadges;
